import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { dehydrate, QueryClient, useQueries } from 'react-query';

import { actionDetailType, searchPriceKeywordSource } from '@common/const';
import { isSameObjKeys } from '@common/lib';
import { getOsType } from '@common/webview';
import { css } from '@emotion/react';
import { RecommendKeywordType } from '@type/api/standardPrice/price';
import { AveragePriceType } from '@type/web/search';

import useRouterHook from '@lib/hook/useRouterHook';
import sizeType from '@lib/styles/sizeType';

import {
  getSearchPriceRecommendKeyword,
  getSearchPriceMainInfo,
  getSearchPriceProduct,
} from '@api/standardPrice.ts/search';

import BasicLayout from '@components/web/Layout/BaisicLayout';
import IconLoading from '@components/web/common/icons/IconLoading';
import useSearchPriceData from '@components/web/search/hook/useSearchPriceData';
import NoResult from '@components/web/search/price/NoResult';
import PriceGraph from '@components/web/search/price/PriceGraph';
import QuickSearchMenu from '@components/web/search/price/QuickSearchMenu';
import RecentSearchRecord from '@components/web/search/price/RecentSearchRecord';
import RecommendKeyword from '@components/web/search/price/RecommendKeyword';
import SearchPriceBar from '@components/web/search/price/SearchPriceBar';

function isString(value: string | string[] | undefined): value is string {
  return !Array.isArray(value) && typeof value !== undefined;
}

const DynamicQuickSearch = dynamic(
  () => import('@components/web/search/price/QuickSearchMenu'),
  { ssr: false },
);

const DynamicRegistered = dynamic(
  () => import('@components/web/search/price/RecentRegisteredProduct'),
  { ssr: false },
);

const DynamicAdProducts = dynamic(
  () => import('@components/web/search/price/AdProduct'),
  { ssr: false },
);

function SeachPriceMain() {
  const { queryString, router, push } = useRouterHook();
  const [isNoResult, setIsNoResult] = useState(false);

  const {
    priceKeyword,
    actionType,
    keywordSource,
    transactionId,
    exposureKeyword,
    searchInfo,
    recommendInfo,
  } = useSearchPriceData();

  useEffect(() => {
    const newQuery = `search-price?actionDetailType=${actionType.text}${
      transactionId ? `&transactionId=${transactionId}` : ''
    }${
      priceKeyword ? `&searchWord=${priceKeyword}` : ''
    }&keywordSource=${keywordSource}${
      exposureKeyword ? `&exposureKeyword=${exposureKeyword}` : ''
    }`;

    window.history.replaceState({}, '', newQuery);
  }, [transactionId, priceKeyword, exposureKeyword, keywordSource, actionType]);

  useEffect(() => {
    if (!searchInfo || !recommendInfo) return;

    if (isSameObjKeys(searchInfo, recommendInfo)) {
      setIsNoResult(true);
    }
  }, [searchInfo, recommendInfo]);

  if (!recommendInfo || (priceKeyword && !searchInfo)) {
    return <IconLoading />;
  }

  return (
    <Fragment>
      <NextSeo
        title={priceKeyword ? `${priceKeyword}` : '시세조회'}
        description={'중고나라에서 최신 인기 상품 시세조회 해봐요!'}
        canonical={`${process.env.NEXT_PUBLIC_DEFAULT_URL}${router.asPath}`}
        openGraph={{
          type: 'website',
          locale: 'ko_KR',
          url: `${process.env.NEXT_PUBLIC_DEFAULT_URL}${router.asPath}`,
          site_name: '중고나라',
          title: priceKeyword
            ? `${priceKeyword}`
            : '2400만명의 선택, 중고나라 - 시세조회',
          description: '중고나라에서 최신 인기 상품 시세조회 해봐요!',
          images: [
            {
              url: 'https://img2.joongna.com/mystore/share/img_jn_url_share%401x.png',
              alt: '중고나라 메인',
              width: 250,
              height: 250,
            },
          ],
        }}
      />

      <BasicLayout
        headerOptions={{
          isShowBackground: true,
          isFixed: { pc: false, tablet: false },
          types: [{ type: 'primary' }, { type: 'navigation' }],
        }}
      >
        <main css={mainWrap}>
          <SearchPriceBar />
          {!searchInfo && recommendInfo && (
            <Fragment>
              <QuickSearchMenu
                heading="이번에는 애플이야! 시세조회 해볼까?"
                description="애플 제품의 시세를 알아보세요"
                titleIcon="betaBadge"
                searchMenuItems={recommendInfo.adminExposure}
              />
              <RecentSearchRecord heading="최근 시세 조회한 검색어" />
              <RecommendKeyword
                heading="인기 시세"
                popularKeywords={recommendInfo?.popularWords}
                recommendKeywords={recommendInfo?.recommendKeywords}
              />
            </Fragment>
          )}

          {
            searchInfo &&
              searchInfo?.emptyResult &&
              searchInfo?.recommendKeywords && (
                // !isNoResult ? (
                <NoResult recommendKeywords={searchInfo?.recommendKeywords} />
              )
            // ) : (
          }
          {isNoResult && (
            <NoResult recommendKeywords={recommendInfo?.recommendKeywords} />
          )}

          {searchInfo && searchInfo.averagePrices && (
            <Fragment>
              {searchInfo.selectOptionName ? (
                <DynamicQuickSearch
                  heading="이번에는 애플이야! 시세조회 해볼까?"
                  description="애플 제품의 시세를 알아보세요"
                  titleIcon="betaBadge"
                  searchMenuItems={recommendInfo.adminExposure}
                  model={searchInfo.selectModelName}
                  option={searchInfo.selectOptionName}
                  avgPrice={searchInfo.averagePrices as AveragePriceType[]}
                />
              ) : (
                <div className="graphContainer">
                  <PriceGraph
                    averagePrices={
                      searchInfo.averagePrices as AveragePriceType[]
                    }
                  />
                </div>
              )}
              <DynamicRegistered
                heading="최근 등록된 상품"
                items={searchInfo.items as []}
              />
              <DynamicAdProducts
                heading="새 상품은 어떠세요?"
                adProductList={
                  searchInfo.standardPriceAdResponse.coupangNaverAdResult
                }
              />
            </Fragment>
          )}
        </main>
      </BasicLayout>
    </Fragment>
  );
}

const mainWrap = css`
  width: 100%;
  max-width: ${sizeType.sizeLayout.large};
  margin: auto;

  .graphContainer {
    @media (max-width: 599px) {
      padding-bottom: 56px;
    }

    @media (min-width: 600px) and (max-width: 1023px) {
      padding-bottom: 56px;
    }

    padding-bottom: 72px;
    > * {
      margin: auto;
    }
  }
`;

export default SeachPriceMain;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const queryClient = new QueryClient();
  const { query } = context;
  const priceKeyword = query?.priceKeyword;
  const actionDetailTypeQuery = query?.actionDetailType;
  const keywordSourceQuery = query?.keywordSource;
  const transactionIdQuery = query?.transactionId;
  const transactionId: string | null = `${transactionIdQuery ?? ''}`;

  const actionType = () => {
    const type = String(actionDetailTypeQuery) as keyof typeof actionDetailType;

    if (type in actionDetailType) {
      return actionDetailType[type];
    }

    return actionDetailType.MARKET_PRICE_CAFE;
  };

  const keywordSource = () => {
    const keyword = keywordSourceQuery as keyof typeof searchPriceKeywordSource;

    if (!keyword) return searchPriceKeywordSource.INPUT_KEYWORD;
    if (keyword in searchPriceKeywordSource) {
      return searchPriceKeywordSource[keyword];
    }

    return searchPriceKeywordSource.INPUT_KEYWORD;
  };

  if (priceKeyword && isString(priceKeyword) && actionType().api === 'MAIN') {
    await queryClient.prefetchQuery(['main', priceKeyword], () =>
      getSearchPriceMainInfo(
        `${priceKeyword}`,
        keywordSource(),
        getOsType(),
        actionType(),
        `${transactionId}`,
      ),
    );
  } else if (
    priceKeyword &&
    isString(priceKeyword) &&
    actionType().api === 'PRODUCT'
  ) {
    await queryClient.prefetchQuery(['product', priceKeyword, actionType], () =>
      getSearchPriceProduct(
        `${priceKeyword}`,
        keywordSource(),
        getOsType(),
        actionType(),
        `${transactionId}`,
      ),
    );
  } else
    await queryClient.prefetchQuery(
      'recommend',
      getSearchPriceRecommendKeyword,
    );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};
